import PropTypes from "prop-types";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { useLayoutEffect } from "react";
import Wrapper from "../../helpers/Wrapper";
import { formatNumberForTooltip } from "../../../utils/NumberFormatter";

am5.addLicense("AM5C258896422");

const CombinedBarLineMultipleAxesChart = ({
  chartId,
  chartClass,
  chartData = [],
  chartLabels = [],
  showLabels = true,
  showLegend = true,
  legendType // Optional, use 'tdp' for TDP Trend Analysis or 'api'
}) => {
  useLayoutEffect(() => {
    if (!chartId || !chartData || chartData.length === 0) return;

    // Create root element
    let root = am5.Root.new(chartId);
    root.setThemes([am5themes_Animated.new(root)]);
    root.interfaceColors.set("grid", am5.color(0xffffff));

    // Use verticalLayout for proper legend placement
    let chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        layout: root.verticalLayout,
        paddingRight: 0,
        paddingTop: 10,
        paddingBottom: 0,
        paddingLeft: 0,
        maxTooltipDistance: -1,
      })
    );

    // Add cursor
    var cursor = chart.set(
      "cursor",
      am5xy.XYCursor.new(root, {
        behavior: "zoomX",
      })
    );
    cursor.lineY.set("visible", false);
    cursor.lineX.set("visible", false);

    // Create axes
    var xRenderer = am5xy.AxisRendererX.new(root, {
      minGridDistance: 100,
      stroke: am5.color(0x707070), // axis line color
      strokeWidth: 1,
      strokeOpacity: 1,
      opposite: false,
      cellStartLocation: 0.25,
      cellEndLocation: 0.75
    });

    let xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "label",
        renderer: xRenderer,
      })
    );
    xAxis.data.setAll(chartData);

    // Configure x-axis labels
    xAxis.get("renderer").labels.template.setAll({
      visible: showLabels,
      fontSize: 13,
      fontFamily: "Ubuntu",
      fill: am5.color(0x757575),
      fontWeight: "400",
      rotation: 0,
      textAlign: "center",
      dy: 10,
      dx: 0,
      oversizedBehavior: "truncate",
      maxWidth: 160,
    });

    // Create y-axes
    var yAxis0 = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {
          stroke: am5.color(0x707070), // axis line color
          strokeWidth: 1,
          strokeOpacity: 1,
        }),
        extraMax: 0.1,
        min: 0,
        numberFormatter: am5.NumberFormatter.new(root, {
          numberFormat: "#.##"
        })
      })
    );
    yAxis0.get("renderer").labels.template.setAll({
      visible: showLabels,
      fontSize: 13,
      fontFamily: "Ubuntu",
      fill: am5.color(0x757575),
      fontWeight: "400",
    });

    var yAxis1 = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {
          opposite: true,
          stroke: am5.color(0x707070), // axis line color
          strokeWidth: 1,
          strokeOpacity: 1,
        }),
        extraMax: 0.1,
        min: 0,
        numberFormatter: am5.NumberFormatter.new(root, {
          numberFormat: "#.##a",
          bigNumberPrefixes: [
            { number: 1e3, suffix: "K" },
            { number: 1e6, suffix: "M" }
          ]
        })
      })
    );
    yAxis1.get("renderer").labels.template.setAll({
      visible: showLabels,
      fontSize: 13,
      fontFamily: "Ubuntu",
      fill: "#FFA000",
      fontWeight: "400",
    });

    // Colors for series
    const colors = {
      promotionalPrice: am5.color(0x085DB6), // Blue
      nonPromotionalPrice: am5.color(0x0972E1), // Light Blue
      promotionalSales: am5.color(0xFF9931), // Orange
      nonPromoSales: am5.color(0xD87723) // Dark Orange
    };

    const colorPalette = [
      am5.color(0x1976D2), // blue
      am5.color(0xFFA000), // orange
      am5.color(0xFF6600), // dark orange
      am5.color(0x43A047), // green
      am5.color(0xC2185B), // pink
    ];

    // Add legend (centered)
    var legend = chart.children.push(
      am5.Legend.new(root, {
        position: "relative",
        x: am5.percent(50),
        centerX: am5.percent(50),
        marginTop: 10,           // Remove extra space above
        marginBottom: 0,        // Remove extra space below
        paddingTop: 2,
        paddingBottom: 2,
        layout: am5.HorizontalLayout.new(root, {
          alignItems: "center",
          justifyContent: "center",
        }),
        useDefaultMarker: true,
      })
    );
    legend.labels.template.setAll({
      fontFamily: "Ubuntu, sans-serif",
      fontSize: 12,
      fill: am5.color(0x213345), // navy blue
      fontWeight: "400",
      oversizedBehavior: "wrap",
      paddingTop: 5,
      paddingBottom: 5,
      paddingLeft: 5,
      paddingRight: 5,
    });
    legend.markers.template.setAll({
      width: 12,
      height: 12,
      strokeWidth: 2,
      fillOpacity: 1,
      strokeOpacity: 1,
      centerY: am5.p50,
      centerX: am5.p50,
    });
    legend.itemContainers.template.setAll({
      layout: root.horizontalLayout,
      alignItems: "center",
      justifyContent: "center",
      paddingTop: 5,
      paddingBottom: 5,
      paddingLeft: 5,
      paddingRight: 5,
    });

    // --- This is the key block for API legend centering ---
    // (Legend type logic omitted for brevity)

    // Create a tooltip with black background and white text
    const createStyledTooltip = (root, labelText) => {
      const tooltip = am5.Tooltip.new(root, {
        labelText: labelText,
        getStrokeFromSprite: false,
        getFillFromSprite: false,
        autoTextColor: false,
      });

      tooltip.label.setAll({
        fill: am5.color(0xffffff),      // White text
        fontSize: 13,
      });

      tooltip.get("background").setAll({
        fill: am5.color(0x213345),      // Black background (can change to 0x000000 if pure black is needed)
        stroke: am5.color(0x000000),
        strokeWidth: 1,
        cornerRadius: 4,
      });

      return tooltip;
    };

    // --- DYNAMIC BAR & LINE SERIES WITH CUSTOM TOOLTIP ---
    let legendSeries = [];
    let barSeriesArr = chartLabels[0]?.barchartLabels || [];
    let lineSeriesArr = chartLabels[0]?.linechartLabels || [];

    const barField = barSeriesArr[0];
    const lineField = lineSeriesArr[0];
    const barColor = "#0972E1";
    const lineColor = "#FFA000";

    // Bar Series
    let barSeries = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: barField.label,
        xAxis: xAxis,
        yAxis: yAxis0,
        valueYField: barField.value,
        categoryXField: "label",
        fill: am5.color(barColor),
        stroke: am5.color(barColor),
        clustered: true,
        tooltip: createStyledTooltip(root, "") // use styled tooltip
      })
    );
    barSeries.columns.template.setAll({
      width: am5.percent(35),
      cornerRadiusTL: 2,
      cornerRadiusTR: 2,
      fillOpacity: 1,
      strokeOpacity: 1,
    });
    barSeries.data.setAll(chartData);
    legendSeries.push(barSeries);

    // Line Series
    let lineSeries = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: lineField.label,
        xAxis: xAxis,
        yAxis: yAxis1,
        valueYField: lineField.value,
        categoryXField: "label",
        stroke: am5.color(lineColor),
        fill: am5.color(lineColor),
        tooltip: createStyledTooltip(root, "") // use styled tooltip
      })
    );
    lineSeries.strokes.template.setAll({
      strokeWidth: 2,
    });
    lineSeries.data.setAll(chartData);
    legendSeries.push(lineSeries);

    // --- Custom tooltip logic: show BOTH bar and line values for the hovered category ---
    [barSeries, lineSeries].forEach(series => {
      series.get("tooltip").adapters.add("labelText", (labelText, target) => {
        const dataItem = target.dataItem;
        if (!dataItem) return "";
        const ctx = dataItem.dataContext;
        return `[bold]${ctx.label}[/]
[font-size:12px][#4264FA]●[/] [bold]${barField.label}: [bold]${formatNumberForTooltip(ctx[barField.value], barField.label)}[/]
[font-size:12px][#FFA000]●[/] [bold]${lineField.label}: [bold]${formatNumberForTooltip(ctx[lineField.value], lineField.label)}[/]`;
      });
    });

    legend.data.setAll(legendSeries);

    // --- Promotion Effectiveness hardcoded series (if NOT TDP) ---
    if (!legendType) {
      try {
        // All custom tooltip logic for all series: we want to show all four values for a given category
        // (promotional_average_price, non_promotional_average_price, promotional_sales_value, non_promotional_sales_value)
        const getPromotionTooltipText = (ctx, root) => {
          return `[bold]${ctx.label}[/]
[font-size:12px][#085DB6]●[bold][/] Promotional Avg Price: [bold]${formatNumberForTooltip(ctx.promotional_average_price ?? 0, "Promotional Avg Price")}[/]
[font-size:12px][#0972E1]●[bold][/] Non-Promo Avg Price: [bold]${formatNumberForTooltip(ctx.non_promotional_average_price ?? 0, "Non-Promo Avg Price")}[/]
[font-size:12px][#FF9931]●[bold][/] Promotional Sales Value: [bold]${formatNumberForTooltip(ctx.promotional_sales_value ?? 0, "Promotional Sales Value")}[/]
[font-size:12px][#D87723]●[bold][/] Non-Promo Sales Value: [bold]${formatNumberForTooltip(ctx.non_promotional_sales_value ?? 0, "Non-Promo Sales Value")}[/]`;
        };

        // Promotional Average Price (Bar)
        const promoPriceSeries = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            name: "Promotional Average Price",
            xAxis: xAxis,
            yAxis: yAxis0,
            valueYField: "promotional_average_price",
            categoryXField: "label",
            fill: colors.promotionalPrice,
            stroke: colors.promotionalPrice,
            clustered: true,
            tooltip: createStyledTooltip(root, "")
          })
        );
        promoPriceSeries.columns.template.setAll({
          width: am5.percent(35),
          cornerRadiusTL: 2,
          cornerRadiusTR: 2,
          fillOpacity: 1,
          strokeOpacity: 1,
        });

        // Non-Promotional Average Price (Bar)
        const nonPromoPriceSeries = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            name: "Without Promotional Average Price",
            xAxis: xAxis,
            yAxis: yAxis0,
            valueYField: "non_promotional_average_price",
            categoryXField: "label",
            fill: colors.nonPromotionalPrice,
            stroke: colors.nonPromotionalPrice,
            clustered: true,
            tooltip: createStyledTooltip(root, "")
          })
        );
        nonPromoPriceSeries.columns.template.setAll({
          width: am5.percent(35),
          cornerRadiusTL: 2,
          cornerRadiusTR: 2,
          fillOpacity: 1,
          strokeOpacity: 1,
        });

        // Promotional Sales Value (Line)
        const promoSalesSeries = chart.series.push(
          am5xy.LineSeries.new(root, {
            name: "Promotional Sales Value",
            xAxis: xAxis,
            yAxis: yAxis1,
            valueYField: "promotional_sales_value",
            categoryXField: "label",
            stroke: colors.promotionalSales,
            fill: colors.promotionalSales,
            tooltip: createStyledTooltip(root, "")
          })
        );
        promoSalesSeries.strokes.template.setAll({
          strokeWidth: 2
        });

        // Non-Promotional Sales Value (Line)
        const nonPromoSalesSeries = chart.series.push(
          am5xy.LineSeries.new(root, {
            name: "Non-Promotional Sales Value",
            xAxis: xAxis,
            yAxis: yAxis1,
            valueYField: "non_promotional_sales_value",
            categoryXField: "label",
            stroke: colors.nonPromoSales,
            fill: colors.nonPromoSales,
            tooltip: createStyledTooltip(root, "")
          })
        );
        nonPromoSalesSeries.strokes.template.setAll({
          strokeWidth: 2
        });

        // Attach custom tooltip logic to all four series
        [promoPriceSeries, nonPromoPriceSeries, promoSalesSeries, nonPromoSalesSeries].forEach(series => {
          series.get("tooltip").adapters.add("labelText", (labelText, target) => {
            const dataItem = target.dataItem;
            if (!dataItem) return "";
            const ctx = dataItem.dataContext;
            return getPromotionTooltipText(ctx, root);
          });
        });

        legend.data.setAll([
          promoPriceSeries,
          nonPromoPriceSeries,
          promoSalesSeries,
          nonPromoSalesSeries
        ]);
      } catch (error) {
        console.error("Error creating chart series:", error);
      }
    }

    chart.series.each((series) => {
      series.data.setAll(chartData);
    });

    chart.appear(1000, 100);

    return () => {
      root.dispose();
    };
  }, [chartData, chartLabels, chartId, showLabels, showLegend, legendType]);

  return (
    <Wrapper>
      <div
        id={chartId}
        className={chartClass}
        style={{
          width: "100%",
          height: "340px",
          minHeight: "320px",
          maxWidth: "99vw"
        }}
      />
    </Wrapper>
  );
};

CombinedBarLineMultipleAxesChart.propTypes = {
  chartId: PropTypes.string,
  chartClass: PropTypes.string,
  chartData: PropTypes.array,
  chartLabels: PropTypes.array,
  showLabels: PropTypes.bool,
  showLegend: PropTypes.bool,
  legendType: PropTypes.string
};

CombinedBarLineMultipleAxesChart.defaultProps = {
  chartData: [],
  chartLabels: [],
  showLabels: true,
  showLegend: true
};

export default CombinedBarLineMultipleAxesChart;